import { useEffect, useState } from "react";
import type { ProtocolDoseUnit } from "@src/packages/injection-protocol/types";
import { Image, View } from "react-native";
import { Video } from "lucide-react-native";

import { IconButton } from "@louise/moon-ui";
import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import { Row } from "@louise/moon-ui/primitives/Row";
import { EMBER, semanticColors } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import { useUserCountry } from "@src/packages/location/useUserCountry";

import {
  isCoverageGap,
  offersCoaching,
  resolveCoachAvailability,
} from "../availability";
import { useInjectionCoach } from "./InjectionCoachHost";

const ns = "care.injection_coach";

const VIDEO_CALL_ILLUSTRATION = require("../assets/video-call.png") as number;

/**
 * The way into guided injection coaching, from a medication surface.
 *
 * A trigger and nothing else. The disclosure and the walk are mounted once at
 * the navigator (`InjectionCoachHost`) rather than here, because a sheet
 * rendered inside a medication row sits inside that row's own `Pressable` —
 * React Native Web propagates along the React tree, so a tap on "a pen" in the
 * device question reached the row and opened the factsheet instead of starting
 * the walk.
 *
 * Opens the walk only where an approved procedure exists for this medication
 * and an escalation is in reach. Both are strict: a coach that cannot reach a
 * person must not offer to guide a needle.
 *
 * An injection with no procedure yet renders greyed and inert, saying so, so
 * that a row without the coach is never mistaken for a row that is not an
 * injection. Anything that is not an injection renders nothing.
 */
export function InjectionCoachEntryButton({
  medicationName,
  routes,
  prescribedDose,
  patientMedicationId,
  apiForm,
  onFormCorrected,
  recordedPackageQuantity,
  onPackageQuantityAnswered,
  hasEarlierDose,
  onCoachFinished,
  onCorrectDose,
  variant = "card",
}: {
  /** The name on her box, used to find the protocol and to name the product. */
  medicationName: string;
  /** Every route recorded for the medication; one self-injected route is enough. */
  routes: readonly (string | null | undefined)[];
  /**
   * The dose her schedule records, passed through to the steps.
   *
   * Absent on a surface with no prescription behind it — the factsheet is a
   * reference page and may be open without one — and the steps then name no
   * number rather than falling back to the product's label dose.
   */
  prescribedDose?: string | null;
  /**
   * The scheduled dose this entry was rendered against, when there is one.
   *
   * Passed to the session record so it names the dose that was coached. Same
   * gate as `onCoachFinished`: a factsheet opened as reference has neither.
   */
  patientMedicationId?: string | null;
  /**
   * What finishing the walk should lead to — usually this surface's dose logger.
   *
   * Supplied by the caller because only it knows which scheduled dose is behind
   * the row. Omitted where there is none, and finishing then simply closes.
   */
  /**
   * `medication.form` as the API stores it, raw.
   *
   * She answered it when she added the medication — the "méthode
   * d'administration" buttons — and the coach used to ask her again at the
   * moment she is holding a syringe. No longer translated on the way: the coach
   * asks its own question from the same five values, so this travels as-is and
   * whatever she answers can go straight back to the calendar.
   */
  apiForm?: string | null;
  /**
   * Store a correction, when she says the recorded device is not what she has.
   *
   * The coach cannot write it: nothing in that package may reach the API except
   * the consent receipt, and a test enforces it. So the surface that knows
   * which record this row is does the write, and this is how it is asked.
   *
   * Omitted on a surface with no prescription behind it, and the correction
   * then applies to this walk only.
   */
  onFormCorrected?: (method: string) => void;
  /**
   * The container size her calendar already holds — `600`, in the protocol's
   * unit.
   *
   * Read by `care` and passed in like every other record here. A value removes
   * the vial question: she is shown what is on file and agrees in one tap,
   * rather than answering the same question every evening.
   *
   * Absent until the API carries the field (dashboard-api #1905).
   */
  recordedPackageQuantity?: { value: number; unit: string } | null;
  /**
   * Store the size she just read off the glass.
   *
   * The twin of `onFormCorrected`. The coach does not write — the surface that
   * knows which record it is does — and this is how it hears the answer.
   * Omitted on a surface with no prescription behind it, and the answer then
   * applies to this walk only.
   */
  onPackageQuantityAnswered?: (size: { value: number; unit: string }) => void;
  /**
   * Whether a dose was already logged against this calendar line recently.
   *
   * Read by `care` and passed in, never fetched here: nothing in this package
   * reaches the API, which is what `no-retention.test.ts` holds. It decides one
   * thing — whether a Menopur multidose vial is already mixed — and `null`
   * means the record could not be read rather than "no dose".
   */
  hasEarlierDose?: boolean | null;
  onCoachFinished?: () => void;
  /**
   * Where a dose she confirms against her box is written to her record.
   *
   * Only a surface that owns the medication can supply it — the factsheet
   * opened against a scheduled dose does. Without it her answer lasts for the
   * walk and she is asked again next time, which is honest if tiresome.
   */
  onCorrectDose?: (dose: { value: number; unit: ProtocolDoseUnit }) => void;
  /**
   * `card` is the full entry point under the factsheet's how-to videos; `icon`
   * is the shortcut on a medication row. One component rather than two so the
   * availability rule can never drift apart between the two places a patient
   * can start from.
   */
  variant?: "card" | "icon";
}) {
  const t = useTranslations();
  const coach = useInjectionCoach();
  // Web only — `onHoverIn`/`onHoverOut` never fire on a touch device, where the
  // icon's accessibility label carries the same sentence for a screen reader.
  const [hovered, setHovered] = useState(false);

  // Her market, because a walk that exists somewhere is not a walk she can be
  // offered here. Read at the entry point rather than inside the resolver so
  // the dependency is visible where the promise is made — the same reason
  // `InjectionCoachHost` reads it beside the call that starts the walk.
  // Her market is her profile country, the same one every feature reads.
  // US stands in until the profile has one, the way the region gate does,
  // so a market-restricted walk is not withheld while the profile loads.
  const countryCode = useUserCountry().countryCode ?? "US";

  /**
   * The record, handed to an open walk when it finally lands.
   *
   * `open()` copies these props once, and the read behind `hasEarlierDose`
   * starts when this surface mounts — so a patient who taps promptly carried
   * `null` for the whole session, and `null` sends her to the mixing steps of
   * a vial that may have been mixed a week ago.
   *
   * Above the early return because it is a hook, and harmless on every row
   * that is not the open one: the host applies it only to the request it is
   * showing, and only until she starts walking.
   */
  const noteEarlierDose = coach?.noteEarlierDose;
  useEffect(() => {
    if (!noteEarlierDose || !patientMedicationId) return;
    noteEarlierDose(patientMedicationId, hasEarlierDose ?? null);
  }, [noteEarlierDose, patientMedicationId, hasEarlierDose]);

  const availability = resolveCoachAvailability({
    routes,
    medicationName,
    countryCode,
  });
  if (!coach) return null;
  // An injection with no procedure here yet is shown greyed, saying so; see
  // `isCoverageGap`. Anything else without a walk renders nothing.
  if (isCoverageGap(availability)) {
    return <CoverageGap variant={variant} />;
  }
  if (!offersCoaching(availability)) return null;

  const open = (event?: { stopPropagation?: () => void }) => {
    // The row beneath owns a press of its own, and a shortcut that also opened
    // the factsheet would put two surfaces on screen at once.
    event?.stopPropagation?.();
    // Both variants are shared primitives (IconButton, Row), which record
    // their own press.
    coach.open({
      medicationName,
      prescribedDose,
      patientMedicationId,
      prescribedForm: apiForm ?? null,
      hasEarlierDose: hasEarlierDose ?? null,
      onFormCorrected,
      recordedPackageQuantity: recordedPackageQuantity ?? null,
      onPackageQuantityAnswered,
      onFinished: onCoachFinished,
      onCorrectDose,
    });
  };

  if (variant === "icon") {
    return (
      /* `position: relative` so the label anchors to the icon rather than to the
         row, which would put it somewhere else on every row width. */
      <View style={{ position: "relative", flexShrink: 0 }}>
        <IconButton
          accessibilityLabel={t(`${ns}.walk_me_through_this_injection`)}
          action="injection_coach_entry_icon"
          onPress={open}
          onHoverIn={() => setHovered(true)}
          onHoverOut={() => setHovered(false)}
          hitSlop={8}
          size="md"
          backgroundColor={semanticColors.accent.tint}
          pressedBackgroundColor={semanticColors.accent.tint}
          pressedOpacity={0.7}
        >
          <Video size={15} color={EMBER.colors.warm} />
        </IconButton>

        {/* The hover label names the tool; the accessibility label above names
            the action. They differ on purpose — a pointer wants to know what
            this is, a screen reader wants to know what activating it does. */}
        {hovered ? (
          <View
            // Not hit-testable: a label that could be hovered would flicker as
            // the pointer crossed onto it.
            pointerEvents="none"
            style={{
              position: "absolute",
              bottom: 38,
              right: 0,
              paddingHorizontal: 10,
              paddingVertical: 6,
              borderRadius: 10,
              backgroundColor: "rgba(57,36,29,0.94)",
              maxWidth: 220,
            }}
          >
            <Text
              className="font-dm-sans"
              numberOfLines={2}
              style={{
                fontSize: 12,
                lineHeight: 16,
                color: semanticColors.onDark.text.primary,
              }}
            >
              {t(`${ns}.step_by_step_guidance`)}
            </Text>
          </View>
        ) : null}
      </View>
    );
  }

  return (
    <Row
      surface="glass"
      leading={
        <Image
          source={VIDEO_CALL_ILLUSTRATION}
          resizeMode="contain"
          style={{ width: 40, height: 40 }}
        />
      }
      leadingSize={40}
      title={t(`${ns}.walk_me_through_this_injection`)}
      subtitle={t(`${ns}.step_by_step_camera_optional`)}
      onPress={() => open()}
      action="injection_coach_entry_card"
      accessibilityLabel={t(`${ns}.walk_me_through_this_injection`)}
    />
  );
}

/**
 * The entry point for an injection the coach does not cover yet: the same
 * shape as the live one, greyed, inert, and honest about why.
 */
function CoverageGap({ variant }: { variant: "card" | "icon" }) {
  const t = useTranslations();
  const [hovered, setHovered] = useState(false);
  const label = t(`${ns}.the_coach_does_not_cover_this_medication_yet`);

  if (variant === "icon") {
    return (
      // Hover is read on the wrapper: a disabled pressable swallows its own
      // hover events on web, and the one sentence this icon has to say would
      // never show to a pointer.
      <View
        style={{ position: "relative", flexShrink: 0 }}
        onPointerEnter={() => setHovered(true)}
        onPointerLeave={() => setHovered(false)}
      >
        <IconButton
          accessibilityLabel={label}
          action="injection_coach_entry_icon_unavailable"
          disabled
          disabledOpacity={0.35}
          hitSlop={8}
          size="md"
          // The muted action's tint rather than the live icon's accent: the
          // same button, visibly not available.
          backgroundColor={semanticColors.action.tonal.background}
          pressedBackgroundColor={semanticColors.action.tonal.background}
        >
          <Video size={15} color={semanticColors.text.secondary} />
        </IconButton>
        {hovered ? (
          <View
            pointerEvents="none"
            style={{
              position: "absolute",
              bottom: 38,
              right: 0,
              paddingHorizontal: 10,
              paddingVertical: 6,
              borderRadius: 10,
              backgroundColor: "rgba(57,36,29,0.94)",
              // A fixed width, not a max: an absolute child of an icon-wide
              // wrapper otherwise shrinks to the icon and wraps every word.
              width: 220,
            }}
          >
            <Text
              className="font-dm-sans"
              numberOfLines={3}
              style={{
                fontSize: 12,
                lineHeight: 16,
                // The live icon's hover label, the same bubble.
                color: semanticColors.onDark.text.primary,
              }}
            >
              {label}
            </Text>
          </View>
        ) : null}
      </View>
    );
  }

  return (
    <Row
      surface="glass"
      disabled
      chevron={false}
      leading={
        <Image
          source={VIDEO_CALL_ILLUSTRATION}
          resizeMode="contain"
          style={{ width: 40, height: 40 }}
        />
      }
      leadingSize={40}
      title={t(`${ns}.walk_me_through_this_injection`)}
      subtitle={label}
      accessibilityLabel={label}
    />
  );
}
